import { create } from 'zustand';

import { API, deleteRequest, getRequest, postRequest, putRequest } from '@/api/api.ts';

import type {
  CreateFolderPayload,
  Folder,
  FoldersActions,
  FoldersState,
  UpdateFolderPayload,
} from './folders.dto';

type FoldersStore = FoldersState & FoldersActions;

const toError = (err: unknown) => (err instanceof Error ? err.message : String(err));

// ponytail: trips became events (MGVN-265), but the backend still names the default folder
// "Trips" and derives its id from that name. Rename here until the communication service does.
const displayName = (name: string) => (name === 'Trips' ? 'Events' : name);

const normalizeFolder = (folder: Folder | { id: string; folder?: string; name?: string }): Folder =>
  ({
    id: folder.id,
    name: displayName((folder as any).name ?? (folder as any).folder ?? ''),
    created_at: (folder as any).created_at,
    updated_at: (folder as any).updated_at,
  }) as Folder;

export const useFoldersStore = create<FoldersStore>((set) => ({
  folders: null,
  isLoading: false,
  error: null,
  getFolders: async () => {
    set({ isLoading: true });
    try {
      const response = await getRequest<Folder[]>(API.CHAT, '/folders');

      set({
        folders: response.data.map((folder) => normalizeFolder(folder)),
        error: null,
      });
    } catch (err) {
      set({ error: toError(err) });
    } finally {
      set({ isLoading: false });
    }
  },
  createFolder: async (name: string) => {
    try {
      const response = await postRequest<Folder, CreateFolderPayload>(API.CHAT, '/folders', {
        folder: name,
      });

      const normalized = normalizeFolder(response.data);
      set((state) => ({
        folders: state.folders ? [...state.folders, normalized] : [normalized],
        error: null,
      }));

      return normalized;
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  updateFolder: async (id: string, name: string) => {
    try {
      const response = await putRequest<Folder, UpdateFolderPayload>(API.CHAT, `/folders/${id}`, {
        folder: name,
      });

      const normalized = normalizeFolder(response.data);
      set((state) => ({
        folders: state.folders
          ? state.folders.map((folder) => (folder.id === id ? normalized : folder))
          : [normalized],
        error: null,
      }));

      return normalized;
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  deleteFolder: async (id: string) => {
    try {
      await deleteRequest<void>(API.CHAT, `/folders/${id}`);

      set((state) => ({
        folders: state.folders ? state.folders.filter((folder) => folder.id !== id) : null,
        error: null,
      }));
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
}));
